/**
 * How the chat shows when something was said.
 *
 * 🔴 ONE INSTANT, STORED IN UTC, SHOWN IN THE VISITOR'S OWN TIME. Every message keeps a single
 *    epoch-milliseconds number, which means the same moment everywhere on earth. It is only
 *    turned into words here, by the browser's own `Intl`, in the timezone and language the visitor's
 *    device is set to. Nothing in the widget does timezone arithmetic by hand, no offsets are
 *    stored, and a chat opened on a laptop in Mumbai and on a phone in London shows each person
 *    their own clock for the same message.
 *
 * 🔴 DAYS ARE COMPARED AS CALENDAR DAYS, NOT AS 24-HOUR BLOCKS. "Yesterday" is the previous date
 *    on the visitor's calendar, so a message at 23:50 and one at 00:10 are on different days even
 *    though they are twenty minutes apart, and a daylight-saving day of 23 or 25 hours is still
 *    one day. The labels are worked out when drawn, never stored, so a chat that said "Today"
 *    reads "Yesterday" when it is opened tomorrow.
 *
 * Every function takes the timezone and the locale as optional arguments. The panel never passes
 * them (the device's own are used); tests pass fixed ones so they do not depend on where the
 * machine running them is.
 */

export interface Zone {
  /** An IANA timezone such as "Asia/Kolkata". Absent means the device's own. */
  readonly timeZone?: string;
  /** A BCP 47 language tag such as "en-GB". Absent means the device's own. */
  readonly locale?: string;
}

/** A moment that is plausible for a chat: after the year 2000 and not more than a day ahead. */
export function isPlausibleTime(at: unknown, now = Date.now()): at is number {
  return typeof at === "number" && Number.isFinite(at) && at > 946_684_800_000 && at <= now + 86_400_000;
}

/** The calendar date of an instant in a timezone, as a day number that can be subtracted. */
function dayNumber(at: number, zone: Zone): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    ...(zone.timeZone ? { timeZone: zone.timeZone } : {}),
    year: "numeric",
    month: "numeric",
    day: "numeric",
  }).formatToParts(new Date(at));
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  return Date.UTC(get("year"), get("month") - 1, get("day")) / 86_400_000;
}

/** Whether two instants fall on the same calendar day for this visitor. */
export function sameDay(a: number, b: number, zone: Zone = {}): boolean {
  return dayNumber(a, zone) === dayNumber(b, zone);
}

/** `3:42 PM`, or `15:42` where the visitor's locale uses a 24-hour clock. */
export function clockLabel(at: number, zone: Zone = {}): string {
  return new Intl.DateTimeFormat(zone.locale, {
    ...(zone.timeZone ? { timeZone: zone.timeZone } : {}),
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(at));
}

/**
 * The full moment with its zone, for the tooltip: "Thursday, 8 October 2026 at 11:45 am IST".
 * The one place the timezone is named, so the short label can stay short without being ambiguous.
 */
export function fullLabel(at: number, zone: Zone = {}): string {
  return new Intl.DateTimeFormat(zone.locale, {
    ...(zone.timeZone ? { timeZone: zone.timeZone } : {}),
    dateStyle: "full",
    timeStyle: "long",
  }).format(new Date(at));
}

/**
 * The heading above a day's messages: "Today", "Yesterday", the weekday for the last
 * few days, then the date, with the year only when it is not this one.
 */
export function dayLabel(at: number, now: number, zone: Zone = {}): string {
  const diff = dayNumber(now, zone) - dayNumber(at, zone);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  const base = { ...(zone.timeZone ? { timeZone: zone.timeZone } : {}) };
  if (diff > 1 && diff < 7) return new Intl.DateTimeFormat(zone.locale, { ...base, weekday: "long" }).format(new Date(at));
  const sameYear = new Intl.DateTimeFormat("en-US", { ...base, year: "numeric" }).format(new Date(at)) ===
    new Intl.DateTimeFormat("en-US", { ...base, year: "numeric" }).format(new Date(now));
  return new Intl.DateTimeFormat(zone.locale, {
    ...base,
    day: "numeric",
    month: "short",
    ...(sameYear ? {} : { year: "numeric" }),
  }).format(new Date(at));
}
